<template>
  <div
    class="gallery flex flex-col sm:flex-row gap-2 md:gap-4 sm:h-[264px] lg:h-[420px] mt-4 sm:mt-6"
  >
    <div class="sm:w-full rounded-lg relative">
      <NuxtImg
        :height="'auto'"
        preload
        :src="String(mainMedia?.src?.mobile)"
        :alt="mainMedia?.alt"
        aspect-ratio="auto"
        loading="eager"
        class="sm:hidden w-full h-full object-cover rounded-lg"
        sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
      >
      </NuxtImg>
      <NuxtImg
        :height="'auto'"
        preload
        :src="String(mainMedia?.src?.desktop)"
        :alt="mainMedia?.alt"
        aspect-ratio="auto"
        loading="eager"
        class="hidden sm:block w-full h-full object-cover rounded-2xl"
        sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
      >
      </NuxtImg>

      <div class="content">
        <h1 class="text-h2 font-semibold">{{ mainMedia.title }}</h1>
        <h2 class="text-base text-stone-700 font-medium mt-.05 mb-4">
          {{ mainMedia.subtitle }}
        </h2>
        <NuxtLazyHydrate when-idle>
          <FederatedSearchLoadingPlaceholder v-show="!isMounted" />
        </NuxtLazyHydrate>
        <NuxtLazyHydrate when-idle>
          <FederatedSearch
            v-show="isMounted"
            element-id="banner-autocomplete"
            analytics-placement="homepage"
          />
        </NuxtLazyHydrate>

        <div class="animation">
          <div class="animation-item animation-item--1">
            <HomeAnimation1 />
          </div>

          <div class="animation-item animation-item--2">
            <HomeAnimation2 />
          </div>

          <div class="animation-item animation-item--3">
            <HomeAnimation3 />
          </div>
        </div>
      </div>
    </div>
    <div class="secondary-cards-container">
      <div
        v-for="(media, index) in secondaryMediaCards"
        :key="index"
        class="card-item"
        :class="{
          'sm:row-span-2': index === 2,
          'lg:h-[202px] sm:hidden': index !== 2,
          'card-item--mobile-video-active':
            media.kind === 'video' && mobileActiveCardIndex === index,
          'card-item--desktop-video-playing':
            media.kind === 'video' && desktopPlayingCardIndex === index,
        }"
        @mouseenter="onCardMouseEnter($event, media, index)"
        @mouseleave="onCardMouseLeave($event, media, index)"
      >
        <component
          :is="media.url ? 'a' : 'div'"
          :href="media.url"
          class="h-full block"
        >
          <template v-if="media.kind === 'video'">
            <div class="banner-gallery-video-stack relative h-full w-full">
              <NuxtImg
                v-if="media.poster?.mobile"
                :src="media.poster.mobile"
                :alt="media.alt"
                loading="lazy"
                class="banner-gallery-poster banner-gallery-poster--mobile"
                sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
              />
              <NuxtImg
                v-if="media.poster?.desktop"
                :src="media.poster.desktop"
                :alt="media.alt"
                loading="lazy"
                class="banner-gallery-poster banner-gallery-poster--desktop"
                sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
              />
              <video
                :ref="(el) => registerMobileVideo(index, el)"
                class="banner-gallery-video banner-gallery-video--mobile"
                muted
                playsinline
                preload="auto"
                :controls="false"
                disablepictureinpicture
                controlslist="nodownload nofullscreen noremoteplayback noplaybackrate"
                :aria-label="media.alt"
                @canplay="onMobileVideoCanPlay(index)"
                @ended="onMobileVideoEnded(index)"
              >
                <source :src="media.src.mobile" type="video/webm" />
              </video>
              <video
                class="banner-gallery-video banner-gallery-video--desktop"
                muted
                loop
                playsinline
                preload="metadata"
                :controls="false"
                disablepictureinpicture
                controlslist="nodownload nofullscreen noremoteplayback noplaybackrate"
                :aria-label="media.alt"
              >
                <source :src="media.src.desktop" type="video/webm" />
              </video>
            </div>
          </template>
          <template v-else>
            <NuxtImg
              :height="'auto'"
              preload
              :src="String(media.src.mobile)"
              :alt="media.alt"
              aspect-ratio="auto"
              loading="lazy"
              class="h-full w-full object-cover sm:hidden"
              sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
            />
            <NuxtImg
              :height="'auto'"
              preload
              :src="String(media.src.desktop)"
              :alt="media.alt"
              aspect-ratio="auto"
              loading="lazy"
              class="h-full w-full object-cover hidden sm:block"
              sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
            />
          </template>
          <div
            class="bg-blend-hard-light bg-gradient-to-t from-stone-700 to-transparent absolute bottom-0 left-0 w-full h-full overflow-hidden opacity-30 md:opacity-0 group-hover:opacity-10 transition-all duration-300"
          ></div>
          <div
            class="title absolute bottom-0 left-0 p-2 w-full font-semibold text-white text-sm"
          >
            {{ media.text }}
          </div>
        </component>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import HomeAnimation1 from '~/assets/animation/home-animation-1.svg?component'
import HomeAnimation2 from '~/assets/animation/home-animation-2.svg?component'
import HomeAnimation3 from '~/assets/animation/home-animation-3.svg?component'
import { useBannerGalleryVideoPlayback } from '~/composables/useBannerGalleryVideoPlayback'

interface SecondaryBannerImageItem {
  kind: 'image'
  src: { mobile: string; desktop: string }
  alt: string
  text?: string
  url?: string
}

interface SecondaryBannerVideoItem {
  kind: 'video'
  src: { mobile: string; desktop: string }
  poster?: { mobile?: string; desktop?: string }
  alt: string
  text?: string
  url?: string
}

type SecondaryBannerMedia = SecondaryBannerImageItem | SecondaryBannerVideoItem

interface StrapiBannerMedia {
  mime?: string
  url?: string
  alternativeText?: string
  formats?: Record<string, { url?: string; ext?: string; hash?: string }>
}

interface SecondaryMediaBlock {
  media?: StrapiBannerMedia | null
  mediaMobile?: StrapiBannerMedia | null
  videoThumbnail?: StrapiBannerMedia | null
  title?: string
  url?: string
}

const strapiMediaUrl = (
  media: StrapiBannerMedia | null | undefined,
  format: 'thumbnail' | 'small' | 'medium' | 'large' | null = null
) => strapiImageGetters.getImageUrl(media as never, format)

const isStrapiVideo = (m: { mime?: string } | null | undefined) =>
  Boolean(m?.mime?.includes('video'))

const isMounted = ref(false)

const homePageGalleryData = inject('homePageGalleryData') as ComputedRef<any>
const bannerData = computed(() => homePageGalleryData.value?.data?.banner)

const mainMedia = computed(() => {
  const mainMediaBlock = bannerData.value?.mainMediaBlock
  const alt = strapiImageGetters.getAlternativeText(mainMediaBlock?.media)

  return {
    src: {
      mobile:
        strapiImageGetters.getImageUrl(mainMediaBlock?.mediaMobile, 'small') ||
        strapiImageGetters.getImageUrl(mainMediaBlock?.media, null),
      desktop: strapiImageGetters.getImageUrl(mainMediaBlock?.media, null),
    },
    alt,
    title: mainMediaBlock?.title,
    subtitle: mainMediaBlock?.subtitle,
  }
})

const secondaryMedia = computed((): SecondaryBannerMedia[] => {
  const blocks = bannerData.value?.secondaryMediaBlocks
  if (!blocks) return []

  return (blocks as SecondaryMediaBlock[])
    .filter((mediaBlock) => {
      const mime = mediaBlock?.media?.mime ?? ''
      const mobileMime = mediaBlock?.mediaMobile?.mime ?? ''
      return (
        mime.includes('image') ||
        mime.includes('video') ||
        mobileMime.includes('video')
      )
    })
    .map((mediaBlock): SecondaryBannerMedia => {
      const main = mediaBlock?.media
      const mobile = mediaBlock?.mediaMobile
      const mainVideo = isStrapiVideo(main)
      const mobileVideo = isStrapiVideo(mobile)

      if (mainVideo || mobileVideo) {
        const desktopSrc =
          strapiMediaUrl(mainVideo ? main : mobile, null) ||
          strapiMediaUrl(main, 'large') ||
          ''
        const mobileSrc =
          (mobileVideo ? strapiMediaUrl(mobile, null) : null) ||
          (mainVideo ? strapiMediaUrl(main, null) : null) ||
          strapiMediaUrl(mobile, 'large') ||
          strapiMediaUrl(main, 'large') ||
          desktopSrc

        const posterFromThumbnail = mediaBlock.videoThumbnail
          ? strapiMediaUrl(mediaBlock.videoThumbnail, 'large') ||
            strapiMediaUrl(mediaBlock.videoThumbnail, null)
          : undefined
        const posterMobile =
          posterFromThumbnail ??
          (mobile && !mobileVideo
            ? strapiMediaUrl(mobile, 'large') || strapiMediaUrl(mobile, null)
            : undefined)
        const posterDesktop =
          posterFromThumbnail ??
          (main && !mainVideo
            ? strapiMediaUrl(main, 'large') || strapiMediaUrl(main, null)
            : undefined)

        return {
          kind: 'video',
          src: { mobile: mobileSrc, desktop: desktopSrc },
          poster:
            posterMobile || posterDesktop
              ? {
                  mobile: posterMobile,
                  desktop: posterDesktop ?? posterMobile,
                }
              : undefined,
          alt:
            strapiImageGetters.getAlternativeText(main as never) ||
            strapiImageGetters.getAlternativeText(mobile as never),
          text: mediaBlock?.title,
          url: mediaBlock?.url,
        }
      }

      return {
        kind: 'image',
        src: {
          mobile: strapiMediaUrl(mobile) || strapiMediaUrl(main, 'large'),
          desktop: strapiMediaUrl(main, 'large'),
        },
        alt: strapiImageGetters.getAlternativeText(main as never),
        text: mediaBlock?.title,
        url: mediaBlock?.url,
      }
    })
})

const secondaryMediaCards = computed(() => secondaryMedia.value.slice(0, 4))

const mobileVideoCardIndices = computed(() =>
  secondaryMediaCards.value.flatMap((media, cardIndex) =>
    media.kind === 'video' && media.src.mobile ? [cardIndex] : []
  )
)

const {
  desktopPlayingCardIndex,
  mobileActiveCardIndex,
  registerMobileVideo,
  onDesktopCardEnter,
  onDesktopCardLeave,
  onMobileVideoEnded,
  onMobileVideoCanPlay,
  bindMobileLayoutListener,
} = useBannerGalleryVideoPlayback(mobileVideoCardIndices)

const onCardMouseEnter = (
  event: MouseEvent,
  media: SecondaryBannerMedia,
  cardIndex: number
) => {
  if (media.kind !== 'video') return
  onDesktopCardEnter(event.currentTarget as HTMLElement, cardIndex)
}

const onCardMouseLeave = (
  event: MouseEvent,
  media: SecondaryBannerMedia,
  cardIndex: number
) => {
  if (media.kind !== 'video') return
  onDesktopCardLeave(event.currentTarget as HTMLElement, cardIndex)
}

onMounted(() => {
  isMounted.value = true
  bindMobileLayoutListener()
})
</script>
<style lang="postcss" scoped>
.content {
  @apply absolute bottom-0 flex flex-col justify-center px-4 pb-4 sm:px-12 left-0 right-0;
  @apply sm:absolute sm:top-0 sm:my-auto sm:h-auto sm:left-auto sm:right-auto;
}

.secondary-cards-container {
  @apply grid grid-rows-1 sm:grid-rows-2 grid-flow-col gap-2 max-sm:auto-cols-fr;
}

.card-item {
  @apply rounded-lg cursor-pointer overflow-hidden relative w-full min-w-0 aspect-[5/8] sm:aspect-auto sm:w-[120px] sm:h-auto lg:w-[202px] md:block;

  img,
  video {
    @apply transition-all duration-300;
  }

  &:hover {
    img,
    video {
      @apply transform scale-105;
    }
  }
}

/* Video stack: poster above video; visibility controlled by card state classes */
.banner-gallery-video-stack {
  @apply relative h-full w-full;
}

.banner-gallery-poster,
.banner-gallery-video {
  @apply absolute inset-0 h-full w-full object-cover;
}

.banner-gallery-poster--mobile,
.banner-gallery-video--mobile {
  @apply sm:hidden;
}

.banner-gallery-poster--desktop,
.banner-gallery-video--desktop {
  @apply hidden sm:block;
}

.banner-gallery-poster {
  z-index: 1;
  @apply transition-opacity duration-300;
}

.banner-gallery-video {
  z-index: 0;
  pointer-events: none;

  &::-webkit-media-controls,
  &::-webkit-media-controls-enclosure,
  &::-webkit-media-controls-panel,
  &::-webkit-media-controls-play-button,
  &::-webkit-media-controls-start-playback-button,
  &::-webkit-media-controls-overlay-play-button,
  &::-webkit-media-controls-timeline,
  &::-webkit-media-controls-current-time-display,
  &::-webkit-media-controls-time-remaining-display,
  &::-webkit-media-controls-mute-button,
  &::-webkit-media-controls-volume-slider,
  &::-webkit-media-controls-toggle-closed-captions-button,
  &::-webkit-media-controls-fullscreen-button {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  &::-moz-media-controls,
  &::-moz-media-controls-panel {
    display: none !important;
  }

  &::-ms-media-controls,
  &::-o-media-controls {
    display: none !important;
  }
}

/* Mobile: one video in playlist — poster hidden, video visible only on active card */
@media (max-width: 639px) {
  .card-item--mobile-video-active .banner-gallery-poster--mobile {
    @apply opacity-0 pointer-events-none;
  }

  .card-item:not(.card-item--mobile-video-active)
    .banner-gallery-video--mobile {
    @apply invisible;
  }
}

/* Desktop: hover — fade poster, video plays underneath */
@media (min-width: 640px) {
  .card-item--desktop-video-playing .banner-gallery-poster--desktop {
    @apply opacity-0 pointer-events-none;
  }
}

.title {
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: -10px;
    background: linear-gradient(-180deg, transparent, rgba(0, 0, 0, 0.7));
    z-index: -1;
  }
}

@keyframes item-animation {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(10deg) translate3d(-5px, 5px, 0);
  }

  40%,
  80% {
    transform: rotate(-15deg) translate3d(-15px, 0px, 0);
  }
}

@keyframes item-animation-small {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(10deg) translate3d(-10px, 3px, 0) scale(1.3);
  }

  40%,
  80% {
    transform: rotate(-10deg) translate3d(-3px, 5px, 0) scale(1.5);
  }
}

@keyframes item-animation-large {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(0deg) translate3d(3px, 5px, 0);
  }

  40%,
  80% {
    transform: rotate(-10deg) translate3d(5px, 0, 0) scale(0.9);
  }
}

.animation {
  @apply absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-1;

  &-item {
    position: absolute;

    &--1 {
      @apply bottom-0 right-[-30px];
      animation: item-animation-large 15s ease infinite;

      @screen lg {
        @apply bottom-auto top-[185px] right-0;
      }
    }

    &--2 {
      @apply top-[120px] left-[0px];
      animation: item-animation-small 15s ease infinite;

      @screen lg {
        @apply top-[260px] left-[20px];
      }
    }

    &--3 {
      @apply top-[-50px] left-[47px];
      animation: item-animation 15s ease infinite;

      @screen lg {
        @apply top-[90px] left-[30px];
      }
    }
  }
}
</style>
